import { BlobProvider } from "@react-pdf/renderer";
import React, { useCallback, useEffect, useState } from "react";
import { QuotePdf } from "./QuotePdf";
import API from "services/axios";
import authHeader from "services/auth-header";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import Spinner from "components/Spinner";
import ReactQuill from "react-quill";
import SelectQuoteEmailTemplate from "./SelectQuoteEmailTemplate";

export default function QuotePdfButton({
  btnClasses,
  selectedQuote,
  btnTitle = "Get Pdf",
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */
  const [showPdf, setShowPdf] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [locationLogo, setLocationLogo] = useState(null);
  const [pdfOptions, setPdfOptions] = useState({
    show_Total: true,
    show_qty: true,
    show_tax_rate: true,
  });

  const [loading, setLoading] = useState(false);
  const [userSmtp, setUserSmtp] = useState(null);
  const [smtpMessage, setSmtpMessage] = useState("");

  const [emailTo, setEmailTo] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
      ["clean"],
    ],
  };

  /*   Template Variable Helpers
   ********************************************* */

  const replaceTemplateVariables = (val) => {
    const quoteId = selectedQuote?.quoteId || "";
    const customerId = selectedQuote?.customer?.customerId || "";
    const customerName = selectedQuote?.customer?.customerName || "";

    let updatedText = val
      .replaceAll("&lt;&lt; Quote ID &gt;&gt;", quoteId)
      .replaceAll("&lt;&lt; ID &gt;&gt;", quoteId)
      .replaceAll("&lt;&lt; Customer ID &gt;&gt;", customerId)
      .replaceAll("&lt;&lt; Customer Name &gt;&gt;", customerName);

    let firstName = `${selectedQuote?.contactFirstName || ""}`;
    let lastName = `${selectedQuote?.contactLastName || ""}`;

    updatedText = updatedText.replaceAll(
      "&lt;&lt; First Name &gt;&gt;",
      firstName,
    );
    updatedText = updatedText.replaceAll(
      "&lt;&lt; Last Name &gt;&gt;",
      lastName,
    );
    updatedText = updatedText.replaceAll(
      "&lt;&lt; Job Name &gt;&gt;",
      selectedQuote?.jobName || "",
    );

    return updatedText;
  };

  const replaceSubjectVariables = (val) => {
    const quoteId = selectedQuote?.quoteId || "";
    const customerId = selectedQuote?.customer?.customerId || "";
    const customerName = selectedQuote?.customer?.customerName || "";

    let updatedText = val
      .replaceAll("<< Quote ID >>", quoteId)
      .replaceAll("<< ID >>", quoteId)
      .replaceAll("<< Customer ID >>", customerId)
      .replaceAll("<< Customer Name >>", customerName);

    let firstName = `${selectedQuote?.contactFirstName || ""}`;
    let lastName = `${selectedQuote?.contactLastName || ""}`;

    updatedText = updatedText.replaceAll("<< First Name >>", firstName);
    updatedText = updatedText.replaceAll("<< Last Name >>", lastName);
    updatedText = updatedText.replaceAll(
      "<< Job Name >>",
      selectedQuote?.jobName || "",
    );

    return updatedText;
  };

  const applyTemplate = (templateData) => {
    const body = templateData?.template ?? templateData?.format ?? "";
    const subject = templateData?.subject || "";
    setEmailBody(replaceTemplateVariables(body));
    setSubject(replaceSubjectVariables(subject));
  };

  /*   All Functions
   ********************************************* */

  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  const getLocationLogo = async () => {
    const { data } = await API.post(
      "/design-request/get-image",
      { path: selectedQuote?.location?.pdfLogo },
      {
        headers: authHeader(),
      },
    );
    setShowPdf(true);
    setLocationLogo(data);
    return data;
  };

  const normalizeEmailHtml = (html) => {
    if (!html) return "";
    let cleaned = html.replace(/\u00A0/g, " ");
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleaned, "text/html");
      doc.querySelectorAll("p").forEach((p) => {
        const textOnly = (p.textContent || "").replace(/\s/g, "");
        const hasOnlyBr =
          p.querySelectorAll("br").length > 0 && textOnly === "";
        if (textOnly === "" && !hasOnlyBr) {
          p.remove();
        } else {
          p.style.margin = "0";
        }
      });
      doc.body.innerHTML = doc.body.innerHTML.replace(
        /(<br\s*\/?>\s*){2,}/gi,
        "<br />",
      );
      return doc.body.innerHTML;
    } catch (err) {
      return cleaned;
    }
  };

  const checkUserLocationSmtp = useCallback(async () => {
    if (!selectedQuote?.locationId) {
      setUserSmtp(null);
      setSmtpMessage(
        "No location found for this quote. Please set location SMTP.",
      );
      return;
    }

    try {
      loadingOn();
      const { data } = await API.post(
        "/user/smtp/check",
        {
          locationId: selectedQuote?.locationId,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success && data?.smtpEmail) {
        setUserSmtp({ smtpEmail: data?.smtpEmail });
        setSmtpMessage("");
      } else {
        setUserSmtp(null);
        setSmtpMessage(
          data?.message ||
            "No SMTP is configured for this location. Please add SMTP settings.",
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      setUserSmtp(null);
      setSmtpMessage(
        err?.message || "No SMTP is configured for this location.",
      );
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  }, [dispatch, selectedQuote?.locationId]);

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    // The quote changed (e.g. prev/next navigation) — reset the generated PDF
    // so the user must re-generate for the NEW quote instead of reusing the
    // previous quote's blob (which would send/show the wrong quote).
    setShowPdf(false);
    setLocationLogo(null);

    setEmailTo(selectedQuote?.contactEmail);
    setSubject(`Quote #${selectedQuote?.quoteId || ""}`);
    setEmailBody("");

    const locationId =
      selectedQuote?.locationId ||
      selectedQuote?.location?.id ||
      selectedQuote?.location?.locationId ||
      null;

    if (!locationId) return;

    (async () => {
      try {
        const { data } = await API.get(
          `/locations/${locationId}/templates/quotes`,
          { headers: authHeader() },
        );
        if ((data?.success || data?.type === "success") && data?.data) {
          applyTemplate(data.data);
        }
      } catch (err) {
        // ignore missing default template
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuote]);

  useEffect(() => {
    if (showPdfModal && selectedQuote?.locationId) {
      checkUserLocationSmtp();
    }
  }, [showPdfModal, selectedQuote?.locationId, checkUserLocationSmtp]);

  return (
    <>
      <button
        className={btnClasses}
        onClick={() => {
          setShowPdfModal(true);
        }}
      >
        {btnTitle}
      </button>

      {/*   Delete Modal
       ********************************************* */}
      <Modal
        show={showPdfModal}
        size="lg"
        onHide={() => setShowPdfModal(false)}
        contentClassName="bg-transparent"
      >
        <Modal.Body className="custom-border-radius-sm bg-white px-3 py-2">
          {loading && <Spinner />}
          <h3>Select Pdf Options</h3>
          <hr />
          <div className="my-3">
            {Object.keys(pdfOptions)?.map((option) => (
              <div
                key={option}
                className="d-flex gap-2 align-items-center mb-2"
              >
                <input
                  onChange={(e) => {
                    setPdfOptions((prev) => {
                      return { ...prev, [option]: e.target.checked };
                    });
                  }}
                  type="checkbox"
                  checked={pdfOptions[option]}
                  id={"invoicePage-" + option}
                  data-switch="success"
                />
                <label
                  htmlFor={"invoicePage-" + option}
                  data-on-label="Yes"
                  data-off-label="No"
                ></label>
                <div className="ms-2 fw-bold">
                  {option?.replaceAll("_", " ")?.toUpperCase()}
                </div>
              </div>
            ))}
          </div>

          {/* Email To input field */}
          <div>
            <label htmlFor="emailTo">Email To</label>
            <input
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="form-control"
              placeholder="Enter email address"
            />
            {!emailTo && (
              <small className="text-danger">
                Email is required ( Please email to quote details )
              </small>
            )}
            {/* Sender info — the address the quote is actually sent from,
                resolved server-side from this location's SMTP account. */}
            {userSmtp?.smtpEmail && (
              <small className="text-muted d-block mt-1">
                Email will be sent using {userSmtp.smtpEmail}
              </small>
            )}
            {!userSmtp?.smtpEmail && smtpMessage && (
              <small className="text-danger d-block mt-1">{smtpMessage}</small>
            )}
          </div>

          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center">
              <label htmlFor="quoteSubject">Subject</label>
              {/* Hidden per request -- Email Template feature hidden in Settings, to be deleted later */}
              <button
                type="button"
                onClick={() => setShowTemplateModal(true)}
                className="d-none btn btn-sm btn-success"
              >
                Select Template
              </button>
            </div>
            <input
              id="quoteSubject"
              value={subject || ""}
              onChange={(e) => setSubject(e.target.value)}
              className="form-control"
              placeholder="Enter subject"
            />
          </div>

          <div className="mt-3">
            <label>Email Body</label>
            <ReactQuill
              modules={modules}
              theme="snow"
              value={emailBody || ""}
              onChange={(val) => setEmailBody(val)}
            />
          </div>

          <div className="d-flex justify-content-end gap-1 mt-3">
            <button
              onClick={() => {
                setShowPdfModal(false);
              }}
              className="btn btn-secondary"
            >
              Close
            </button>
            {showPdf && locationLogo ? (
              <>
                <BlobProvider
                  document={
                    <QuotePdf
                      selectedQuote={selectedQuote}
                      locationLogo={locationLogo}
                      showTotal={pdfOptions.show_Total}
                      showQty={pdfOptions.show_qty}
                      showTaxRate={pdfOptions.show_tax_rate}
                    />
                  }
                >
                  {({ url, blob, error }) => {
                    const sendEmail = async () => {
                      if (!userSmtp?.smtpEmail) {
                        return;
                      }
                      if (!emailTo) {
                        return alert("Email To is required!");
                      }
                      loadingOn();
                      const formData = new FormData();
                      formData.append("file", blob);
                      formData.append("quoteId", selectedQuote?.quoteId);
                      formData.append("emailTo", emailTo);
                      formData.append("locationId", selectedQuote?.locationId);
                      formData.append("subject", subject || "");
                      formData.append(
                        "emailBody",
                        normalizeEmailHtml(emailBody || ""),
                      );
                      // formData.append(
                      //   "locationName",
                      //   selectedQuote?.location?.name,
                      // );

                      try {
                        const { data } = await API.post(
                          "/quote-email",
                          formData,
                          {
                            headers: {
                              ...authHeader(),
                              "Content-Type": "multipart/form-data",
                            },
                          },
                        );
                        loadingOff();
                        if (data?.type === "success") {
                          dispatch(
                            setNotification({
                              message: data.message,
                              type: "success",
                            }),
                          );
                          loadingOff();
                          setShowPdfModal(false);
                          return;
                        }
                        if (data?.type === "error") {
                          dispatch(
                            setNotification({
                              message: data.message,
                              type: "error",
                            }),
                          );
                          loadingOff();
                          setShowPdfModal(false);
                          return;
                        }
                        dispatch(
                          setNotification({
                            message: "Something Went wrong check console.",
                            type: "error",
                          }),
                        );

                        loadingOff();
                      } catch (err) {
                        dispatch(
                          setNotification({
                            message: err.response.data.message,
                            type: "error",
                          }),
                        );
                        loadingOff();
                      }
                    };
                    const isDisabled = !url || !blob || loading;
                    return (
                      <div className="d-flex gap-1">
                        {/*
                          View & Download moved out to the 3-dots menu
                          (QuotePdfMenu). Kept here, commented, in case we want
                          them back inside this modal later.

                        <a
                          href={isDisabled ? "#" : url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none text-white btn btn-primary"
                          onClick={(e) => {
                            if (isDisabled) e.preventDefault();
                          }}
                        >
                          <i className="mdi mdi-eye"></i> View
                        </a>
                        <a
                          href={isDisabled ? "#" : url}
                          download={"Q-" + (selectedQuote?.quoteId || "download") + ".pdf"}
                          className="text-decoration-none text-white btn btn-success"
                          onClick={(e) => {
                            if (isDisabled) e.preventDefault();
                          }}
                        >
                          <i className="mdi mdi-download"></i> Download
                        </a>

                        */}
                        <button
                          disabled={
                            !!error ||
                            !url ||
                            !emailTo ||
                            !userSmtp?.smtpEmail ||
                            !subject
                          }
                          onClick={sendEmail}
                          className="btn btn-info"
                          title={
                            error
                              ? "PDF failed to generate"
                              : !url
                                ? "Generating PDF…"
                                : !emailTo
                                  ? "Enter an email address"
                                  : !subject
                                    ? "Enter a subject"
                                    : !userSmtp?.smtpEmail
                                      ? "No SMTP configured for this location"
                                      : ""
                          }
                        >
                          {!url && !error ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Generating PDF…
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-email"></i> Send Email
                            </>
                          )}
                        </button>
                      </div>
                    );
                  }}
                </BlobProvider>
              </>
            ) : (
              <>
                <div>
                  {selectedQuote?.quoteProducts?.length === 0 ? (
                    <button className="btn btn-danger">No Products</button>
                  ) : (
                    <button
                      className="btn btn-success"
                      onClick={getLocationLogo}
                    >
                      Generate Pdf
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        style={{ zIndex: 100004 }}
      >
        <Modal.Body className="bg-white custom-shadow custom-border-radius">
          <h4 className="h3 mb-3">Select Email Template</h4>
          <SelectQuoteEmailTemplate
            showModal={setShowTemplateModal}
            applyTemplate={applyTemplate}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
